import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, H4, InputModal, P } from '../../../lib/components'
import { displayDollar } from '../../../lib/utils/helpers'
import { WithdrawalStatus } from '../../../lib/types'
import { withdrawalServices } from '../../../services'
import { RootState } from '../../../store'
import '../../dashboard-new/cancel-investment-modal.scss'

type CancelWithdrawalModalProps = {
  isOpen: boolean
  onClose: () => void
  withdrawalId: number
  projectName: string
  withdrawalAmount: string
  completedSteps: number
  onCancelSuccess: () => void
}

type WithdrawalCancelErrorResponse = {
  data?: {
    code?: string
    message?: string
  }
}

const getRedemptionCancelErrorMessage = (response?: WithdrawalCancelErrorResponse): string => {
  if (response?.data?.message) return response.data.message
  return 'Failed to cancel redemption. Please try again or contact support.'
}

export const CancelWithdrawalModal: React.FC<CancelWithdrawalModalProps> = ({
  isOpen,
  onClose,
  withdrawalId,
  projectName,
  withdrawalAmount,
  completedSteps,
  onCancelSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const authToken = useSelector((state: RootState) => state.investor?.authToken)

  const handleConfirmCancel = async () => {
    if (!authToken) {
      setError('Authentication required. Please refresh and try again.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await withdrawalServices.patchOpportunityWithdrawal(
        withdrawalId,
        {
          status: WithdrawalStatus.CANCELLED,
          metadata: { completedSteps },
        },
        { Authorization: authToken },
      )

      if (response?.status === 200) {
        onCancelSuccess()
      } else {
        setError(getRedemptionCancelErrorMessage(response))
      }
    } catch (err) {
      console.error('Error cancelling redemption:', err)
      setError(getRedemptionCancelErrorMessage(err as WithdrawalCancelErrorResponse))
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <InputModal
      testId='cancel-withdrawal-modal-backdrop'
      onBackdropClick={() => {
        if (!isLoading) onClose()
      }}
    >
      <div className='cancel-investment-modal' data-testid='cancel-withdrawal-modal'>
        <div className='cancel-investment-modal-header'>
          <H4 text='Cancel Redemption' />
        </div>

        <div className='cancel-investment-modal-content'>
          <P
            text={`Are you sure you want to cancel your redemption request for "${projectName}"?`}
            variant='default-gray'
          />

          <div className='cancel-investment-modal-details'>
            <div className='investment-detail-row'>
              <span className='label'>Redemption Amount:</span>
              <span className='value'>{displayDollar(withdrawalAmount, 'USD', 2, true)}</span>
            </div>
            <div className='investment-detail-row'>
              <span className='label'>Project:</span>
              <span className='value'>{projectName}</span>
            </div>
          </div>

          <div className='cancel-investment-modal-warning'>
            <P
              text='This action cannot be undone. Your redemption request will be removed from in-progress redemptions.'
              variant='default-red'
            />
          </div>

          {error ? (
            <div className='cancel-investment-modal-error'>
              <P text={error} variant='default-red' />
            </div>
          ) : null}
        </div>

        <div className='cancel-investment-modal-actions'>
          <Button
            text='No, Keep Redemption'
            variant='secondary-btn'
            handleClick={onClose}
            isDisabled={isLoading}
            testId='cancel-withdrawal-modal-keep-btn'
          />
          <Button
            text={isLoading ? 'Cancelling...' : 'Yes, Cancel Redemption'}
            variant='primary-btn'
            handleClick={handleConfirmCancel}
            isDisabled={isLoading}
            testId='cancel-withdrawal-modal-confirm-btn'
            classes={['danger-button']}
          />
        </div>
      </div>
    </InputModal>
  )
}
